import type { AppDispatch } from '@/store';
import { adaptFormFieldValuesToSectionFormFieldUpdateRequest } from '@/adapters';
import type { AddressLinkDTO } from '@/api/base/DTOs';
import { config } from '../config';
import type {
  FormField,
  FormInitialValues,
  IndexedConditionalChildFields,
  IndexedConditionalFieldsGroups,
  IndexedStagedFieldsGroups,
} from '../typings';
import { setConditionalChildFieldHasAnyValue } from '../store';
import {
  forgetSentValues,
  markSentValue,
  unmarkSentValue,
} from '../save-queue/sent-values';
import { decodeAddressLink, indexAddressGroups } from '../utils/addressLink';

export const shouldSkipFormChangeFieldKey = (fieldId: string): boolean => {
  if (fieldId.endsWith(config.shouldValidateFieldSuffix)) return true;
  if (fieldId.endsWith(config.validateFirstStageFieldSuffix)) return true;
  if (fieldId.endsWith(config.requiredFieldSuffix)) return true;
  if (fieldId.endsWith(config.instanceHasValueFieldSuffix)) return true;
  if (fieldId.endsWith(config.atLeastOneCompleteFieldSuffix)) return true;
  if (fieldId.endsWith(config.atLeastOneChildWithValueFieldSuffix)) return true;
  if (fieldId.endsWith(config.atLeastOneChildAnsweredYesFieldSuffix))
    return true;
  if (fieldId.endsWith(config.requiredWhenValueFieldSuffix)) return true;
  if (fieldId.endsWith(config.minFilesCountFieldSuffix)) return true;
  if (fieldId.endsWith(config.limitRuleFieldSuffix)) return true;
  if (fieldId.endsWith(config.addressFieldsShownFieldSuffix)) return true;
  return false;
};

export const getEffectiveFieldId = (fieldId: string): string =>
  fieldId.endsWith(config.officeHoursOnCallFieldSuffix)
    ? fieldId.replace(config.officeHoursOnCallFieldSuffix, '')
    : fieldId;

export const stripNotApplicableSuffix = (fieldId: string): string =>
  fieldId.replace(config.notApplicableFieldSuffix, '');

export const isAddressLinkFieldKey = (fieldId: string): boolean =>
  fieldId.endsWith(config.addressLinkFieldSuffix);

export const stripAddressLinkSuffix = (fieldId: string): string =>
  fieldId.slice(0, -config.addressLinkFieldSuffix.length);

export const findConditionFieldGroup = (
  effectiveFieldId: string,
  conditionalFieldsGroups: IndexedConditionalFieldsGroups,
): { isConditionField: boolean; conditionFieldGroupId: string | undefined } => {
  const baseId = stripNotApplicableSuffix(effectiveFieldId);
  for (const [groupId, conditionFieldId] of Object.entries(
    conditionalFieldsGroups,
  )) {
    if (conditionFieldId === baseId) {
      return { isConditionField: true, conditionFieldGroupId: groupId };
    }
  }
  return { isConditionField: false, conditionFieldGroupId: undefined };
};

export const computeSectionFieldFlags = (
  effectiveFieldId: string,
  stagedFieldsGroups: IndexedStagedFieldsGroups,
): {
  isStagedField: boolean;
  isParentStagedField: boolean;
} => {
  const baseId = stripNotApplicableSuffix(effectiveFieldId);

  const isParentStagedField = Object.keys(stagedFieldsGroups).includes(baseId);

  let isStagedField = false;
  for (const stagedFieldsGroupIds of Object.values(stagedFieldsGroups)) {
    if (stagedFieldsGroupIds.includes(baseId)) {
      isStagedField = true;
      break;
    }
  }

  return { isStagedField, isParentStagedField };
};

/** The adapted body a field update posts. Named so both transports refer to one type. */
export type SectionFormFieldUpdateRequest = ReturnType<
  typeof adaptFormFieldValuesToSectionFormFieldUpdateRequest
>;

/** One server write a batch of changed values implies, before any transport is chosen. */
export type FormChangeRequest =
  | {
      kind: 'notApplicable';
      /** The changed field this write came from — used to put it back on the queue. */
      sourceFieldId: string;
      /** Field id as the form produced it, N/A suffix included. */
      rawFieldId: string;
      /** `rawFieldId` without the N/A suffix — the id the server knows. */
      itemId: string;
      value: unknown;
      isParentStagedField: boolean;
    }
  | {
      kind: 'normalField';
      sourceFieldId: string;
      formField: SectionFormFieldUpdateRequest;
    }
  | {
      kind: 'conditionGroup';
      sourceFieldId: string;
      groupID: string;
      values: SectionFormFieldUpdateRequest['values'];
    }
  | {
      kind: 'addressLink';
      sourceFieldId: string;
      /** The group's field id, as the server knows it. */
      itemId: string;
      link: AddressLinkDTO;
    };

/** A Redux-only visibility flag; it never reaches the server. */
export type ConditionalChildFlag = { fieldId: string; hasAnyValue: boolean };

export type FormChangePlan = {
  requests: FormChangeRequest[];
  /** Changed keys left unsent because the server clears them, by the key whose write clears them. */
  serverClearedFieldIds: Record<string, string[]>;
  childFlags: ConditionalChildFlag[];
  /** Last non-N/A field in the batch — drives scroll-to-last-edited-field. */
  lastEditedField: string | null;
  anyStagedField: boolean;
};

export type FormChangePlanContext = {
  shouldComputeStagedFields: boolean;
  formFieldGroups?: FormField[];
  conditionalChildFields: IndexedConditionalChildFields;
  conditionalFieldsGroups: IndexedConditionalFieldsGroups;
  stagedFieldsGroups?: IndexedStagedFieldsGroups;
};

const hasMeaningfulChanges = (changedValuesObject: FormInitialValues) => {
  const changedValues = Object.values(changedValuesObject);
  return (
    changedValues.length > 0 &&
    !changedValues.every(value => value === undefined)
  );
};

/** Base field ids ticked Not Applicable in this batch, each mapped to the key that ticks it. */
const findFieldIdsFlaggedNotApplicable = (
  changedValuesObject: FormInitialValues,
): Map<string, string> =>
  new Map(
    Object.entries(changedValuesObject)
      .filter(
        ([fieldId, value]) =>
          fieldId.includes(config.notApplicableFieldSuffix) && value === true,
      )
      .map(([fieldId]) => [stripNotApplicableSuffix(fieldId), fieldId]),
  );

/** Field ids a "Same as" answer in this batch hides, each mapped to the key holding it. */
const findFieldIdsHiddenByAddressLinks = (
  changedValuesObject: FormInitialValues,
  formFieldGroups: FormField[] | undefined,
): Map<string, string> => {
  const hidden = new Map<string, string>();

  const linkKeys = Object.entries(changedValuesObject)
    .filter(
      ([fieldId, value]) =>
        isAddressLinkFieldKey(fieldId) &&
        (decodeAddressLink(value)?.target ?? 'Own') !== 'Own',
    )
    .map(([fieldId]) => fieldId);

  if (linkKeys.length === 0 || !formFieldGroups) return hidden;

  const groups = indexAddressGroups(formFieldGroups);

  for (const linkKey of linkKeys) {
    groups
      .get(stripAddressLinkSuffix(linkKey))
      ?.address.hiddenFieldIds.forEach(id => hidden.set(id, linkKey));
  }

  return hidden;
};

/** The changed key whose write clears `effectiveFieldId` on the server, if any. */
const findClearingFieldKey = (
  effectiveFieldId: string,
  clearingKeysByBaseId: Map<string, string>,
): string | undefined => {
  for (const [baseId, clearingKey] of clearingKeysByBaseId) {
    if (isUnderNotApplicableField(effectiveFieldId, new Set([baseId]))) {
      return clearingKey;
    }
  }
  return undefined;
};

/**
 * True when a changed field id is a Not Applicable item or one of its descendants.
 * Child ids are parent-prefixed paths (`ApplicationFormItemHelper.ConvertToFormItemDTO`
 * passes `"{parentId}_"` down), so a subtree is exactly the `${baseId}_` prefix.
 */
export const isUnderNotApplicableField = (
  effectiveFieldId: string,
  naBaseIds: Set<string>,
): boolean => {
  if (naBaseIds.has(effectiveFieldId)) return true;

  for (const naBaseId of naBaseIds) {
    const prefix = `${naBaseId}_`;
    if (!effectiveFieldId.startsWith(prefix)) continue;

    // Not Applicable details are filled *because* of the tick — the provider types them into a box
    // that only appears once the box is ticked, so their values are new, not stale, and must be sent.
    if (
      effectiveFieldId.startsWith(
        `${prefix}${config.notApplicableDetailsIdentifier}_`,
      )
    )
      continue;

    return true;
  }

  return false;
};

/**
 * Decides what a batch of changed values means: which writes it implies and which
 * Redux flags go with them. Synchronous and free of side effects — it reads the
 * lookup slice through the adapter but writes nothing — so the unload path can
 * consume it inside a `pagehide` handler, where awaiting anything loses the request.
 */
export const planFormChangeRequests = (
  changedValuesObject: FormInitialValues,
  ctx: FormChangePlanContext,
): FormChangePlan => {
  if (!hasMeaningfulChanges(changedValuesObject)) {
    // A fresh literal, not a shared constant — a future caller that mutates
    // one empty plan (e.g. `.push` on `requests`) must not corrupt every
    // other empty plan.
    return {
      requests: [],
      serverClearedFieldIds: {},
      childFlags: [],
      lastEditedField: null,
      anyStagedField: false,
    };
  }

  const requests: FormChangeRequest[] = [];
  const serverClearedFieldIds: Record<string, string[]> = {};
  const childFlags: ConditionalChildFlag[] = [];

  const recordServerCleared = (clearingKey: string, fieldId: string) => {
    serverClearedFieldIds[clearingKey] = [
      ...(serverClearedFieldIds[clearingKey] ?? []),
      fieldId,
    ];
  };

  const fieldIdsFlaggedNotApplicable =
    findFieldIdsFlaggedNotApplicable(changedValuesObject);

  // The server clears these on the link and refuses writes to them while linked, so their
  // value changes in this batch are already stale.
  const fieldIdsHiddenByAddressLinks = findFieldIdsHiddenByAddressLinks(
    changedValuesObject,
    ctx.formFieldGroups,
  );

  let lastEditedField: string | null = null;
  let anyStagedField = false;

  for (const [fieldId, fieldValue] of Object.entries(changedValuesObject)) {
    if (shouldSkipFormChangeFieldKey(fieldId)) continue;

    const effectiveFieldId = getEffectiveFieldId(fieldId);

    if (!fieldId.includes(config.notApplicableFieldSuffix)) {
      lastEditedField = effectiveFieldId;
    }

    const { isConditionField, conditionFieldGroupId } = findConditionFieldGroup(
      effectiveFieldId,
      ctx.conditionalFieldsGroups,
    );

    let isStagedField = false;
    let isParentStagedField = false;

    if (ctx.shouldComputeStagedFields && ctx.stagedFieldsGroups) {
      const flags = computeSectionFieldFlags(
        effectiveFieldId,
        ctx.stagedFieldsGroups,
      );
      isStagedField = flags.isStagedField;
      isParentStagedField = flags.isParentStagedField;
      if (isStagedField) {
        anyStagedField = true;
      }
    }

    if (fieldId.includes(config.notApplicableFieldSuffix)) {
      requests.push({
        kind: 'notApplicable',
        sourceFieldId: fieldId,
        rawFieldId: fieldId,
        // Strip only the N/A suffix via the helper, exactly as each strategy did
        // before this extraction — NOT via getEffectiveFieldId, which also strips
        // office hours.
        itemId: stripNotApplicableSuffix(fieldId),
        value: fieldValue,
        isParentStagedField,
      });
      continue;
    }

    if (isAddressLinkFieldKey(fieldId)) {
      const itemId = stripAddressLinkSuffix(fieldId);
      const link = decodeAddressLink(fieldValue);
      const clearingNotApplicableKey = findClearingFieldKey(
        itemId,
        fieldIdsFlaggedNotApplicable,
      );

      // A blank answer stays local: the server resets links itself when it clears a group.
      if (link && !clearingNotApplicableKey) {
        requests.push({
          kind: 'addressLink',
          sourceFieldId: fieldId,
          itemId,
          link,
        });
      } else if (link && clearingNotApplicableKey) {
        recordServerCleared(clearingNotApplicableKey, fieldId);
      }
      continue;
    }

    const childField = ctx.conditionalChildFields[effectiveFieldId];

    if (childField && !isStagedField) {
      childFlags.push({
        fieldId: effectiveFieldId,
        hasAnyValue:
          fieldValue !== undefined && fieldValue !== null && fieldValue !== '',
      });
    }

    // Ticking Not Applicable clears the item and everything under it on the server, so the paired
    // value changes in this batch are already stale — sending them would be rejected. A "Same as"
    // answer does the same to the fields it hides.
    const clearingKey =
      findClearingFieldKey(effectiveFieldId, fieldIdsFlaggedNotApplicable) ??
      findClearingFieldKey(effectiveFieldId, fieldIdsHiddenByAddressLinks);

    if (clearingKey) {
      // Staged children are never autosaved, so the screen keeps owning them instead.
      if (!isStagedField) recordServerCleared(clearingKey, fieldId);
      continue;
    }

    const normalUpdateAllowed =
      (!ctx.shouldComputeStagedFields && !isConditionField) ||
      (ctx.shouldComputeStagedFields && !isStagedField && !isConditionField);

    if (normalUpdateAllowed && ctx.formFieldGroups) {
      requests.push({
        kind: 'normalField',
        sourceFieldId: fieldId,
        formField: adaptFormFieldValuesToSectionFormFieldUpdateRequest(
          fieldValue,
          fieldId,
          ctx.formFieldGroups,
        ),
      });
    }

    if (isConditionField && conditionFieldGroupId && ctx.formFieldGroups) {
      const formField = adaptFormFieldValuesToSectionFormFieldUpdateRequest(
        fieldValue,
        fieldId,
        ctx.formFieldGroups,
      );
      requests.push({
        kind: 'conditionGroup',
        sourceFieldId: fieldId,
        groupID: conditionFieldGroupId,
        values: formField.values,
      });
    }
  }

  return {
    requests,
    serverClearedFieldIds,
    childFlags,
    lastEditedField,
    anyStagedField,
  };
};

export type FormChangeLoopContext = {
  dispatch: AppDispatch;
  /** The save queue key this batch came from; what it sends is recorded under it. */
  saveKey: string;
  formFieldGroups?: FormField[];
  conditionalChildFields: IndexedConditionalChildFields;
  conditionalFieldsGroups: IndexedConditionalFieldsGroups;
  stagedFieldsGroups?: IndexedStagedFieldsGroups;
};

export type FormChangeLoopStrategy = {
  shouldComputeStagedFields: boolean;
  handleNotApplicable: (
    rawFieldId: string,
    value: unknown,
    flags: { isParentStagedField: boolean },
  ) => Promise<boolean>;
  handleNormalFieldUpdate: (
    formField: SectionFormFieldUpdateRequest,
  ) => Promise<boolean>;
  handleConditionGroupUpdate: (args: {
    groupID: string;
    values: SectionFormFieldUpdateRequest['values'];
  }) => Promise<boolean>;
  /** Absent where the API takes no address choice; such a save then fails. */
  handleAddressLink?: (args: {
    itemId: string;
    link: AddressLinkDTO;
  }) => Promise<boolean>;
  afterLoop: (args: {
    isReloadRequired: boolean;
    lastEditedField: string | null;
    anyStagedField: boolean;
  }) => Promise<void>;
};

/**
 * Unwraps an update thunk's result into `reloadRequired`, throwing if the save
 * did not land. The thunks resolve a boolean on every success path and
 * `undefined` only after dispatching a failure, so the type is the signal.
 */
export const requireAccepted = (resp: unknown, itemId: string): boolean => {
  if (typeof resp === 'boolean') return resp;
  throw new Error(`Save rejected for ${itemId}`);
};

/** Forwards one planned request to the strategy that knows how to send it. */
const runFormChangeRequest = (
  request: FormChangeRequest,
  strategy: FormChangeLoopStrategy,
): Promise<boolean> => {
  switch (request.kind) {
    case 'notApplicable':
      return strategy.handleNotApplicable(request.rawFieldId, request.value, {
        isParentStagedField: request.isParentStagedField,
      });
    case 'normalField':
      return strategy.handleNormalFieldUpdate(request.formField);
    case 'conditionGroup':
      return strategy.handleConditionGroupUpdate({
        groupID: request.groupID,
        values: request.values,
      });
    case 'addressLink':
      if (!strategy.handleAddressLink) {
        return Promise.reject(
          new Error(`No address choice endpoint for ${request.itemId}`),
        );
      }
      return strategy.handleAddressLink({
        itemId: request.itemId,
        link: request.link,
      });
  }
};

export const runFormChangeHandlerLoop = async (
  changedValuesObject: FormInitialValues,
  ctx: FormChangeLoopContext,
  strategy: FormChangeLoopStrategy,
): Promise<boolean> => {
  forgetSentValues(ctx.saveKey);

  if (!hasMeaningfulChanges(changedValuesObject)) return true;

  const plan = planFormChangeRequests(changedValuesObject, {
    shouldComputeStagedFields: strategy.shouldComputeStagedFields,
    formFieldGroups: ctx.formFieldGroups,
    conditionalChildFields: ctx.conditionalChildFields,
    conditionalFieldsGroups: ctx.conditionalFieldsGroups,
    stagedFieldsGroups: ctx.stagedFieldsGroups,
  });

  plan.childFlags.forEach(flag => {
    ctx.dispatch(setConditionalChildFieldHasAnyValue(flag));
  });

  let isReloadRequired = false;
  let allAccepted = true;

  for (const request of plan.requests) {
    // Marked before the request, not on its answer: a snapshot in that answer already holds
    // these values, so the screen must not keep laying them over it (the SSN mask).
    const sentFieldIds = [
      request.sourceFieldId,
      ...(plan.serverClearedFieldIds[request.sourceFieldId] ?? []),
    ];
    sentFieldIds.forEach(fieldId =>
      markSentValue(ctx.saveKey, fieldId, changedValuesObject[fieldId]),
    );

    try {
      // requireAccepted throws inside the strategy when the thunk did not resolve a
      // boolean. That throw — not a return value — is the failed-save signal.
      const reload = await runFormChangeRequest(request, strategy);
      if (reload) isReloadRequired = true;
    } catch {
      sentFieldIds.forEach(fieldId => unmarkSentValue(ctx.saveKey, fieldId));
      allAccepted = false;
    }
  }

  try {
    await strategy.afterLoop({
      isReloadRequired,
      lastEditedField: plan.lastEditedField,
      anyStagedField: plan.anyStagedField,
    });
  } catch (error) {
    // afterLoop only refreshes already-persisted state. A failure here must not
    // mark the batch unsaved — that would make the queue re-send field updates
    // the server already accepted and then report an error the user cannot act on.
    console.error('Post-save refresh failed', error);
  }

  return allAccepted;
};
